import DeleteIcon from "@mui/icons-material/Delete";
import TranslateIcon from '@mui/icons-material/Translate';
import { Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Container, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomDialog from "../components/CustomDialog";
import CustomToolTip from "../components/CustomToolTip";
import ImageBox from "../components/ImageBox";
import LanguagesCheckbox from "../components/LanguagesCheckbox";
import useSnackbar from '../components/snackbar/useSnackBar';
import TranslationTextFields from "../components/TranslationTextFields";
import DataTable from "../data/DataTable";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";
import { AI_CLASSES, API_URL, getAttributeIcon, getIfcTypes, IFC_CLASS_TYPES } from "../utils/lib";
import DraggableList from "./DraggableList";

export default function EditObject({ userInfo }) {
  const { id } = useParams();

  const location = useLocation();
  const { fromTab } = location.state || {};

  const [data, setData] = useState({
    name: '',
    localization: {
      de: '',
      en: '',
    },
    attributes: [],
    ifcObject: {
      ifcClass: null,
      ifcType: null
    }
  });
  const [selectedAttribute, setSelectedAttribute] = useState({ key: '', type: 'TEXT', publicFlag: true });
  const [previewPicture, setPreviewPicture] = useState({});
  const [deletePreviewPicture, setDeletePreviewPicture] = useState(false);
  const [openPreviewPictureChooser, setOpenPreviewPictureChooser] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState([]);
  // ref that references all translations
  const translationsRef = useRef();
  // if translations should be shown or not
  const [showTranslations, setShowTranslations] = useState(false);
  const loading = open && attributeOptions.length === 0;
  const attributeId = useRef(-1);

  // if all attributes (language-independent) should be shown
  const [allLanguages, setAllLanguages] = useState(false);

  const isStatic = id !== 'new' && !userInfo?.isAdmin && data.ownerId !== userInfo?.ownerId;

  // locale for language
  const { locale } = useLanguage();
  const intl = useIntl();
  const api = useApi();

  const navigate = useNavigate();

  const { openSnackBar } = useSnackbar();

  const getNextAttributeId = () => {
    return attributeId.current--;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const obj = { ...data, localization: getLanguageFields(data) };
    // field 'name' is not needed for server-communication (done through translations)
    delete obj.name;
    delete obj.previewPicture;
    obj.attributes = getAttributeData();
    if (previewPicture.id !== undefined) {
      obj.previewPicture = { id: previewPicture.id }
    }
    setIsLoading(true);
    if (id === "new") {
      api
        .post(`/object`, obj)
        .then((response) => {
          navigate(`/objects${fromTab != null ? `?tab=${fromTab}` : ''}`);
        })
        .catch((error) => {
          // handle errors
          console.log(error);
          if (error.response.status === 409) {
            openSnackBar(intl.formatMessage({ id: "EditObject.saveConflict" }));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // check if there are attribute-relations that must be deleted beforehand
      let previewPictureDeletionPromise = new Promise((resolve, reject) => resolve());
      if (deletePreviewPicture) {
        previewPictureDeletionPromise = api
          .delete(`/object/${id}/previewPicture`);
      }
      previewPictureDeletionPromise
        .then(() => {
          return api
            .put(`/object/${id}`, obj)
        })
        .then((response) => {
          navigate(`/objects${fromTab != null ? `?tab=${fromTab}` : ''}`);
        })
        .catch((error) => {
          // handle errors
          console.log(error);
          if (error.response.status === 409) {
            openSnackBar(intl.formatMessage({ id: "EditObject.saveConflict" }));
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  /**
   * Transforms the attributes that have been changed to objects that can be used in requests.
   * @returns the attributes
   */
  const getAttributeData = () => {
    return data.attributes.map(attribute => {
      let at = { ...attribute };
      const required = at.required;
      const valueRequired = at.valueRequired;
      if (at.uploaded) {
        // attribute is already uploaded
        return at;
      }
      if (at.attribute.id < 0) {
        // if the attribute is newly defined, set its id to null
        at.attribute.id = null;
      }
      return { ...at, required, valueRequired };
    }).filter(at => at !== null);
  }

  useEffect(() => {
    let active = true;
    if (id !== "new") {
      let fetchURL = '';
      fetchURL += `/object/${id}?localization=true&lang=${locale}`
      setIsLoading(true);
      api
        .get(fetchURL)
        .then(response => {
          if (!active) return;
          parseData(response.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          if (!active) return;
          setIsLoading(false);
        });
    }
    return () => {
      // cancel state-setting-methods
      active = false;
    }
  }, [id, locale]);

  useEffect(() => {
    fetchAllAttributes();
  }, [allLanguages, locale]);

  /**
   * Parse the fetched data.
   * @param {*} data 
   */
  const parseData = (data) => {
    setData(() => {
      return {
        ...data,
        attributes: data.attributes.map(at => ({ ...at, attribute: { ...at.attribute } }))
      }
    });
    if (data?.previewPicture?.id !== undefined) {
      setPreviewPicture({ ...data.previewPicture, notUploaded: false, url: `${API_URL}/object/${id}/previewPicture` });
    }
  }

  /**
   * Fetches all attributes from the server.
   */
  const fetchAllAttributes = () => {
    api.get(`/attribute/all?published=true&all=false&allLanguages=${allLanguages}&lang=${locale}`)
      .then(response => {
        setAttributeOptions([...response.data].map(at => ({ ...at, uploaded: true })));
      })
      .catch(error => {
        console.log(error);
      });
  }

  /**
   * Handle, when the key of the attribute changes.
   * @param {*} value 
   */
  const handleAttributeKeyChange = (value) => {
    setSelectedAttribute(attribute => {
      let obj = {};
      if (value.key) {
        // the attribute is predefined (fetched from the server)
        obj = { ...value };
      } else {
        // the attribute is not defined yet, set type to 'TEXT' as default
        obj = { type: 'TEXT', key: value, publicFlag: true };
      }
      return obj;
    });
  };

  /**
   * Places the specified attribute on the atToDelete-list, if association already on server.
   * Removes the attribute from the data.
   * @param {*} attribute 
   */
  const handleAttributeDelete = (attribute) => () => {
    setData(old => ({ ...old, attributes: old.attributes.filter(at => at.attribute.id !== attribute.id) }));
  }

  /**
   * Adds the specified attribute to the attribute-list of this object.
   * @returns 
   */
  const addAttribute = (selectedAttribute) => {
    if (!selectedAttribute.key || !selectedAttribute.type) return;
    let obj;
    // check if there is at least one attribute with the exact same name
    const atMatch = attributeOptions.filter(at => at.key === selectedAttribute.key);
    if (!selectedAttribute.id && atMatch.length > 0) {
      obj = { attribute: { ...atMatch[0] } };
    } else {
      obj = { attribute: { ...selectedAttribute } };
    }
    // check if the attribute is already associated with the object
    if (data.attributes.filter(at => at.attribute.id === obj.id).length > 0) {
      return;
    }
    if (data.attributes.filter(at => at.attribute.key === obj.key && at.attribute.type === obj.type).length > 0) {
      return;
    }

    obj.required = false;
    obj.valueRequired = false;

    if (obj.attribute.id == null) {
      obj.attribute.id = getNextAttributeId();
    }
    setData(oldData => ({ ...oldData, attributes: [obj, ...oldData.attributes] }));
    setSelectedAttribute({ key: '', type: 'TEXT', publicFlag: true });
  }

  /**
   * Reorders the attributes by moving the attribute at position oldPos to newPos.
   * newPos may be -1 and then indicates that the attribute should be pushed to the end of the array.
   * @param {*} oldPos 
   * @param {*} newPos 
   */
  const reorderAttributes = (oldPos, newPos) => {
    setData(oldData => {
      const attributes = oldData.attributes;
      const [removedEl, _] = attributes.splice(oldPos, 1);
      if (newPos === -1) {
        attributes.push(removedEl);
      } else if (oldPos < newPos) {
        attributes.splice(newPos - 1, 0, removedEl);
      } else {
        attributes.splice(newPos, 0, removedEl);
      }
      return { ...oldData, attributes: [...attributes] }
    });
  }

  /**
   * Handles change of the 'required'-checkbox.
   * If the value is set to false, 'valueRequired' is also set to false.
   * @param {*} id 
   * @param {*} value 
   */
  const handleRequiredCheckboxChange = (id, value) => {
    let obj = { 'required': value };
    if (!value) {
      obj.valueRequired = false;
    }
    setData(oldData => {
      let attributes = oldData.attributes.map(at => {
        if (at.attribute.id === id) {
          return { ...at, ...obj };
        } else {
          return { ...at };
        }
      });
      return { ...oldData, attributes };
    });
  }

  /**
   * Handles change of a generic checkbox.
   * @param {*} id 
   * @param {*} value 
   * @param {*} key 
   */
  const handleCheckboxChange = (id, value, key) => {
    setData(oldData => {
      let attributes = oldData.attributes.map(at => {
        if (at.attribute.id === id) {
          return { ...at, [key]: value };
        } else {
          return { ...at };
        }
      });
      return { ...oldData, attributes };
    });
  };

  /**
   * Selects the specified data-object as preview-picture.
   * @param {*} data 
   * @returns 
   */
  const handleSelectPreviewPicture = (data) => () => {
    data.notUploaded = true;
    data.url = `${API_URL}/data/${data.id}/file`
    setPreviewPicture(data);
    setOpenPreviewPictureChooser(false);
  };

  /**
   * Removes the currently selected preview-picture.
   */
  const handleDeletePreviewPicture = () => {
    if (previewPicture.notUploaded) {
      // must not delete from server
      setPreviewPicture({});
    } else {
      // delete in next submit
      setPreviewPicture({});
      setDeletePreviewPicture(true);
    }
  };

  /**
   * Change the IFC-class and reset the IFC-type.
   * @param {*} value 
   */
  const handleChangeIfcClass = (value) => {
    setData(old => ({ ...old, ifcObject: { ifcClass: value || null, ifcType: null } }))
  };

  /**
   * Change the IFC-type.
   * @param {*} value 
   */
  const handleChangeIfcType = (value) => {
    setData(old => ({ ...old, ifcObject: { ...old.ifcObject, ifcType: value || null } }))
  };

  /**
   * Change the AI-classes.
   * @param {*} values 
   */
  const handleChangeAIClasses = (values) => {
    setData(old => ({ ...old, aiClasses: values }));
  }

  /**
   * A list of all attribute-elements that are associated with the object.
   */
  const attributeElements = useMemo(() => {
    return data.attributes.map(a => {
      const at = a.attribute;
      return <Grid key={at.id} xs={12} container direction="row" alignItems="center" justifyContent="flex-start" spacing={2} >
        <Grid item xs={6} sm={4} md={2} >
          {at.key}
        </Grid>
        <Grid item xs={2} sm={1} md={1} style={{ display: 'flex', alignItems: 'center' }} >
          {getAttributeIcon(at.type)}
        </Grid>
        {!isStatic && userInfo?.isAdmin && <Grid item xs={2} sm={5} md={2} >
          <Button
            variant="outlined"
            onClick={handleAttributeDelete(at)}
            color="error"
            aria-label="delete"
          >
            <DeleteIcon />
          </Button>
        </Grid>}
        {userInfo?.isAdmin && <Grid item xs={6} sm={3} md={3}>
          <FormControlLabel control={
            <Checkbox disabled={isStatic} checked={a.required} onChange={(event) => handleRequiredCheckboxChange(at.id, event.target.checked)} />
          } label={intl.formatMessage({ id: "EditObject.attributeRequired" })} />
        </Grid>}
        {userInfo?.isAdmin && <Grid item xs={6} sm={3} md={3} >
          <FormControlLabel control={
            <Checkbox disabled={!a.required || isStatic} checked={a.valueRequired} onChange={(event) => handleCheckboxChange(at.id, event.target.checked, "valueRequired")} />
          } label={intl.formatMessage({ id: "EditObject.attributeValueRequired" })} />
        </Grid>}
      </Grid>
    });
  }, [data]);

  // a filtered list of all attribute-options, that does not include the already selected attributes
  const filteredAttributeOptions = useMemo(() => {
    const ids = data.attributes.map(at => at.attribute.id).filter(at => at);
    return attributeOptions.filter(at => !ids.includes(at.id));
  }, [data, attributeOptions]);

  /**
     * Returns an object with key-value-pairs of translations.
     * If showTranslations = true, the specific translations are used.
     * If showTranslations = false, only uses the current locale.
     * @param {*} data 
     * @returns 
     */
  const getLanguageFields = (data) => {
    if (showTranslations) {
      // translationsRef is used
      if (translationsRef.current) {
        return translationsRef.current.getTranslations();
      } else {
        return {};
      }
    } else {
      // translationsRef is not used
      return { ...data.localization }
    }
  };

  /**
   * Sets the language-field of the current locale.
   * data.name and data.name<Language> is updated.
   * @param {*} value 
   */
  const setLocaleField = (value) => {
    setData(old => {
      const o = { ...old };
      o.localization[locale] = value;
      o.name = value;
      return o;
    });
  };

  /**
   * The menu-items for the ifc-class-selection.
   */
  const ifcClassMenuItems = useMemo(() => {
    return Object.keys(IFC_CLASS_TYPES).sort((a, b) => a.localeCompare(b)).map((el, index) => <MenuItem key={index} value={el} >{el}</MenuItem>);
  }, [locale]);

  /**
   * The menu-items for the ifc-type-selection.
   */
  const ifcTypeMenuItems = useMemo(() => {
    return getIfcTypes(data.ifcObject.ifcClass).map((el, index) => <MenuItem key={index} value={el} >{el}</MenuItem>);
  }, [locale, data.ifcObject.ifcClass]);

  const textFieldStyle = {
    width: '90%'
  }

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
        <FormattedMessage id="EditObject.heading" />
      </Typography>
      <CustomDialog
        title={intl.formatMessage({ id: "EditObject.addPreviewPicture" })}
        content={<DataTable selectable={true} usageFlags={{ image: true }} onSelect={handleSelectPreviewPicture} />}
        isOpen={openPreviewPictureChooser}
        handleAccept={() => setOpenPreviewPictureChooser(false)}
        handleClose={() => setOpenPreviewPictureChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <p style={{ color: 'gray' }} ><FormattedMessage id="Object.ofWhat" /></p>
      <Box
        component="form"
        onSubmit={handleSubmit}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          sx={{ paddingBottom: 2, marginBottom: 2 }}
        >
          <Grid container flexDirection='column' justifyContent='center' alignItems='center' item xs={12} md={6} >
            {previewPicture.url && <ImageBox url={previewPicture.url} uploaded={previewPicture.notUploaded !== true} onDelete={handleDeletePreviewPicture} disabled={isStatic} />}
            {!isStatic && <Button variant="outlined" sx={{ marginTop: 1 }} onClick={() => setOpenPreviewPictureChooser(true)} ><FormattedMessage id="EditObject.addPreviewPicture" /></Button>}
          </Grid>
        </Box>
        <Grid container spacing={3} alignItems="center" sx={{ border: '1px dashed black', borderRadius: 3, margin: 0, width: "100%", paddingBottom: 2 }}>
          {
            showTranslations &&
            <TranslationTextFields ref={translationsRef} defaultTranslations={{ ...data.localization }}
              isStatic={isStatic} />
          }
          {
            !showTranslations &&
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                required
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                name={"name"}
                label={intl.formatMessage({ id: "EditObject.name" })}
                type="text"
                sx={textFieldStyle}
                value={data.name || ''}
                onChange={(event) => setLocaleField(event.target.value)}
                disabled={isStatic}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
          }
          {
            <Grid item xs={12} container alignItems={'center'}>
              <CustomToolTip
                tooltip={showTranslations ? <FormattedMessage id='EditObject.hideTranslations' /> : <FormattedMessage id='EditObject.showTranslations' />}
                placement={"bottom"}
              >
                <Button
                  onClick={() => setShowTranslations(old => {
                    if (old) {
                      // update localization if switching from multi-translation-text-fields to single field
                      if (translationsRef.current) {
                        const localization = translationsRef.current.getTranslations();
                        setData(data => ({ ...data, name: localization[locale], localization }));
                      }
                    }
                    return !old;
                  })}
                  aria-label='show translations'
                >
                  <TranslateIcon fontSize='large' />
                </Button>
              </CustomToolTip>
            </Grid>
          }
          {!isStatic && <Grid item xs={12}>
            <Typography variant="h6" gutterBottom><FormattedMessage id="EditObject.attributes" /></Typography>
          </Grid>}
          {!isStatic && userInfo?.isAdmin && <Grid item xs={12} container spacing={3} alignItems="center" >
            <Grid item xs={12} sm={6} md={5} >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Autocomplete
                  freeSolo
                  style={{ width: '100%' }}
                  value={selectedAttribute}
                  inputValue={selectedAttribute.key}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  onInputChange={(event, value) => {
                    // is called every time the visible value changes
                    handleAttributeKeyChange(value);
                  }}
                  onChange={(event, value) => {
                    // is called when an option is selected from the dropdown
                    if (!value) {
                      // in case of clearing the option, the value is null
                      handleAttributeKeyChange('');
                      return;
                    }
                    addAttribute(value);
                  }}
                  onKeyDown={(event, value) => {
                    if (event.key === 'Enter') {
                      // when enter is pressed, try to add the object
                      event.preventDefault();
                      // lose focus
                      event.target.blur();
                      addAttribute(selectedAttribute);
                    }
                  }}
                  options={filteredAttributeOptions}
                  getOptionLabel={(option) => {
                    if (option.key !== undefined) {
                      return option.key;
                    } else {
                      return option;
                    }
                  }}
                  id={`combo-box-key`}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      variant="outlined"
                      size="small"
                      {...params}
                      label={intl.formatMessage({ id: "AttributeBox.key" })}
                      sx={textFieldStyle}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    let icon = getAttributeIcon(option.type);
                    return (
                      <li {...props} key={option.id} style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        {option.key}
                        {icon}
                      </li>
                    )
                  }}
                >
                </Autocomplete>
                {!isStatic && <LanguagesCheckbox initialValue={allLanguages} onChange={setAllLanguages} disabled={isStatic} style={{ marginRight: '10px' }} />}
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3} >
              <Button variant="outlined" onClick={() => addAttribute(selectedAttribute)} ><FormattedMessage id="EditObject.addAttribute" /></Button>
            </Grid>
          </Grid>}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom><FormattedMessage id="EditObject.attributeList" /></Typography>
          </Grid>
          <Grid item xs={12} direction="row" spacing={2}>
            <DraggableList reorderList={reorderAttributes} isStatic={isStatic} >
              {attributeElements}
            </DraggableList>
          </Grid>
          <Grid item xs={12} sm={8} md={6}>
            <FormControl style={{ width: '90%' }} >
              <InputLabel><FormattedMessage id="EditObject.ifcClass" /></InputLabel>
              <Select
                label={intl.formatMessage({ id: "EditObject.ifcClass" })}
                value={data.ifcObject.ifcClass || ''}
                onChange={(event) => handleChangeIfcClass(event.target.value)}
                disabled={isStatic}
              >
                <MenuItem value={''}><em><FormattedMessage id="EditObject.none" /></em></MenuItem>
                {ifcClassMenuItems}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} md={6}>
            <FormControl style={{ width: '90%' }} >
              <InputLabel><FormattedMessage id="EditObject.ifcType" /></InputLabel>
              <Select
                label={intl.formatMessage({ id: "EditObject.ifcType" })}
                value={data.ifcObject.ifcType || ''}
                onChange={(event) => handleChangeIfcType(event.target.value)}
                disabled={isStatic}
              >
                <MenuItem value={''}><em><FormattedMessage id="EditObject.none" /></em></MenuItem>
                {ifcTypeMenuItems}
              </Select>
            </FormControl>
          </Grid>
          {
            userInfo?.isAdmin &&
            <Grid item xs={12} sm={6} md={4} key="g1" >
              <Autocomplete
                sx={{ m: 0, width: '80%' }}
                multiple
                id="tags-filled"
                options={AI_CLASSES}
                defaultValue={[]}
                value={data.aiClasses || []}
                onChange={(_, value) => {
                  handleChangeAIClasses(value);
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <div style={{}} >
                    <TextField
                      {...params}
                      variant="outlined"
                      label={intl.formatMessage({ id: "EditObject.aiClasses" })}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50
                      }}
                    />
                    {
                      <div style={{
                        marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '2px',
                        border: '1px dashed', borderRadius: '15px', padding: '10px', width: '100%'
                      }}>
                        {data.aiClasses && data.aiClasses.map((option, index) => {
                          return <Chip
                            key={option}
                            variant="outlined"
                            label={option}
                            onDelete={() => {
                              // remove this option
                              const values = data.aiClasses.filter((_, i) => i !== index);
                              handleChangeAIClasses(values);
                            }}
                          />
                        })}
                      </div>
                    }
                  </div>
                )}
              />
            </Grid>
          }
          {
            userInfo?.isAdmin && id === 'new' &&
            [<Grid key="b1" xs={12}></Grid>,
            <Grid item xs={12} sm={6} md={4} key="g5" >
              <TextField
                required={false}
                sx={textFieldStyle}
                id="id-picker"
                label={intl.formatMessage({ id: "EditObject.manualId" })}
                variant="outlined"
                type="number"
                inputProps={{ min: 1, max: 99999, step: 1 }}
                value={data.id || ''}
                onChange={event => setData(old => ({ ...old, id: event.target.value }))}
              >
              </TextField>
            </Grid>]
          }
        </Grid>
        {!isStatic && <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>}
      </Box>
    </Container>
  );
}
